import type {
	AvatarColorVariantTypes,
	CardGridPropsTypes,
	DashDeviceWidgetPropsTypes,
	DashWidgetGridPropsTypes,
} from '@Shelly/shelly-design-system/index';
import type { DashWidgetEditType, DashWidgetTypes } from './dash-types';
import { normalizeDeviceCardConfig } from './dash-device-card-config';

export { normalizeDeviceCardConfig } from './dash-device-card-config';
import { devices } from 'src/stores/lists/devices';
import { groups } from 'src/stores/lists/groups';
import { rooms } from 'src/stores/lists/rooms';
import { alarms, temp_schemes } from 'src/stores/lists/scenes-derived';
import { get } from 'svelte/store';
import { dashboards } from 'src/stores/features/dashboards';
import { postReq } from 'src/services/http';
import { defaultStateType } from 'src/stores/device-runtime/states';
import { sortByPosition } from '../Lists/list-helpers';
import { format } from 'svelte-i18n';
import { scenes } from 'src/stores/lists/scenes';
import { getRandomDashLightWidgetBg } from './dash-lights-helpers';
import type { Device } from 'src/types/Device';
import { edit } from 'src/stores/ui/edit_lists';
import { user_data, user_settings } from 'src/stores/account/user';
import { NON_PREMIUM_WIDGETS_LIMIT, PREMIUM_WIDGETS_LIMIT } from 'src/constants';
import { convertSeconds, formatTwoDigits } from 'src/helpers/timer_helpers';
import { load_image_from } from 'src/helpers/interface_helpers';
import { mapOldCustomIconFromDBtoNew } from 'src/helpers/new-layout-helpers';
import { normalizeCameraConfig } from './dash-camera-helpers';
import { genEnergyWidgetData, getEnergyWidgetTitleProposal } from './dash-energy-helpers';
import {
	DASH_ENERGY_WIDGET_TYPES,
	isDashEnergyWidgetType,
	isPredefinedDashboardId,
	PREDEFINED_DASHBOARD_IDS,
	type DashEnergyWidgetTypes,
} from './dash-energy-types';
import { ENERGY_WIDGET_REGISTRY } from './dash-energy-registry';
import { getEnergyDashboardIdFromRouteSegment } from 'src/components/EnergyMonitoring/helpers/energy-dashboard-settings';

export { isPredefinedDashboardId, PREDEFINED_DASHBOARD_IDS };

export const DEFAULT_DASH_ID = -1;

/** Resolve dashboard id from explicit prop, route params, or semantic energy routes. */
export const resolveDashboardId = (options: {
	explicitId?: string | number | null | undefined;
	paramsId?: string | number | null | undefined;
	location?: string | null | undefined;
}): string | number => {
	const { explicitId, paramsId, location } = options;
	if (
		explicitId !== undefined &&
		explicitId !== null &&
		explicitId !== '' &&
		String(explicitId) !== String(DEFAULT_DASH_ID)
	) {
		return explicitId;
	}

	const routeSegment =
		paramsId !== undefined && paramsId !== null && paramsId !== ''
			? String(paramsId)
			: location?.split('/').filter(Boolean)?.[1];

	if (!routeSegment) return DEFAULT_DASH_ID;

	const energyDashboardId = getEnergyDashboardIdFromRouteSegment(routeSegment);
	if (energyDashboardId !== null) return energyDashboardId;

	return routeSegment;
};

export const countDashboardWidgets = (dashboardId: string | number): number => {
	const dash = get(dashboards)[dashboardId];
	return dash?.dashWidgets?.length ?? 0;
};

export const getDashboardWidgetsLimit = (dashboardId: string | number | undefined | null): number => {
	if (isPredefinedDashboardId(dashboardId)) return Number.POSITIVE_INFINITY;
	const isPremium = get(user_data).premium || get(user_settings).been_premium;
	return isPremium ? PREMIUM_WIDGETS_LIMIT : NON_PREMIUM_WIDGETS_LIMIT;
};

export const canAddDashboardWidget = (
	dashboardId: string | number | undefined | null,
	options: { isEdit?: boolean } = {},
): boolean => {
	if (options.isEdit) return true;
	if (
		dashboardId === undefined ||
		dashboardId === null ||
		dashboardId === '' ||
		Number(dashboardId) === DEFAULT_DASH_ID
	) {
		return true;
	}
	if (isPredefinedDashboardId(dashboardId)) return true;
	return countDashboardWidgets(dashboardId) < getDashboardWidgetsLimit(dashboardId);
};

export const MAX_CARDS_IN_WIDGET = 12;

export const staticCardGridConfig: Partial<CardGridPropsTypes & { class?: string }> = {
	class: 'pb-m',
	gridGap: 'l',
	sm: 100,
	widthUnit: '%',
	disableGridMaxHeight: true,
	gridRowMinContent: true,
	forceGridGapOnMobile: true,
};

// Shared min column widths for DashDeviceQuickControl, DashDeviceCard, and DeviceList.
// Density rationale: DEVICE_WIDGET_GRID_DENSITY.md
export const gidBreakpointsDeviceCardWidget: DashWidgetGridPropsTypes = {
	xl: 190,
	lg: 180,
	sm: 160,
};

export const gridConfigOptions: { [key: string]: DashWidgetGridPropsTypes } = {
	full: {
		sm: 100,
		widthUnit: '%',
	},
	s: gidBreakpointsDeviceCardWidget,
	m: {
		sm: 260,
	},
	half: {
		sm: 100,
		lg: 50,
		widthUnit: '%',
	},
	quarter: {
		sm: 100,
		md: 50,
		lg: 25,
		widthUnit: '%',
	},
};

const energyWidgetGridConfigMapper = Object.fromEntries(
	DASH_ENERGY_WIDGET_TYPES.map((type) => [type, gridConfigOptions[ENERGY_WIDGET_REGISTRY[type].grid]]),
) as Record<DashEnergyWidgetTypes, DashWidgetGridPropsTypes>;

export const dashWidgetGridConfigMapper: {
	[key in DashWidgetTypes]: DashWidgetGridPropsTypes;
} = {
	dashWeather: gridConfigOptions.full,
	dashMedia: gridConfigOptions.full,
	dashInfo: gridConfigOptions.full,
	dashLight: gridConfigOptions.m,
	dashParameterDev: gridConfigOptions.s,
	dashQuickDev: gridConfigOptions.s,
	dashCardDev: gridConfigOptions.s,
	dashCamera: gridConfigOptions.full,
	dashChart: gridConfigOptions.full,
	dashGroup: gridConfigOptions.m,
	dashRoom: gridConfigOptions.m,
	dashScene: gridConfigOptions.m,
	dashAlarm: gridConfigOptions.m,
	dashControlDev: gridConfigOptions.m,
	dashThermostat: gridConfigOptions.m,
	...energyWidgetGridConfigMapper,
	dashEnvironment: gridConfigOptions.full,
	dashPromo: gridConfigOptions.full,
};

export const getDashWidgetGridConfig = (widget): DashWidgetGridPropsTypes => {
	if (widget?.type !== 'dashCamera') {
		return dashWidgetGridConfigMapper[widget?.type] || gridConfigOptions.full;
	}

	switch (widget?.cameraConfig?.variant) {
		case 'device-control':
		case 'button-card':
			return gridConfigOptions.s;
		case 'triple-focus':
		case 'full-preview':
		default:
			return gridConfigOptions.full;
	}
};

export const getNumericKeys = (obj: object): (number | string)[] => Object.keys(obj);

export const isNonNullable = (item) => item != null;

export const genWidgetId = (dashID, widgetType: DashWidgetEditType['widgetType'], counter = 0) => {
	return `${dashID}-${widgetType}-${counter}`;
};

export const genChunks = (list) => {
	const chunkSize = MAX_CARDS_IN_WIDGET;
	const chunks = Array.from({ length: Math.ceil(list.length / chunkSize) }, (_, i) =>
		list.slice(i * chunkSize, i * chunkSize + chunkSize),
	);

	return chunks;
};

export const deviceWithControlCondition = (deviceID: string | number) => {
	return (
		get(devices)[deviceID] &&
		get(devices)[deviceID]?.category !== 'media_player' &&
		get(devices)[deviceID]?.category !== 'sensor' &&
		get(devices)[deviceID]?.category !== 'inputs_reader'
	); // Media player is other widget category)
};

export const deviceWithNoMediaCondition = (deviceID: string | number) => {
	return get(devices)[deviceID] && get(devices)[deviceID]?.category !== 'media_player';
};

export const filterDevicesWithControl = (deviceList: Device[]) => {
	return deviceList.filter((device) => deviceWithControlCondition(device.id));
};

export const filterDevicesWithNoMediaPlayer = (deviceList: Device[]) => {
	return deviceList.filter((device) => deviceWithNoMediaCondition(device.id)); // Media player is other widget category)
};

//#startregion Convert Legacy to new dash
export const convertLegacyToNewAccInfoWidget = (dashID: string) => {
	const widgetType: DashWidgetTypes = 'dashInfo';

	return [
		widgetCardsDataMapper[widgetType]({
			id: genWidgetId(dashID, widgetType),
			title: get(format)('label_special_acc_info_data_card', {
				default: 'Account Information',
			}),
			type: widgetType,
			col: 'left',
		}),
	];
};

export const convertLegacyToNewWeatherWidget = (weatherItems, dashID: string) => {
	if (!weatherItems || weatherItems?.length === 0) {
		return [];
	}

	const widgetType: DashWidgetTypes = 'dashWeather';
	const weatherItemIds = Object.keys(weatherItems);

	return weatherItemIds.map((id, i) => {
		return widgetCardsDataMapper[widgetType]({
			id: genWidgetId(dashID, widgetType, i),
			position: weatherItems[id]?.position || 0,
			type: widgetType,
			title: get(format)('label_special_weather_data_card', {
				default: 'Weather',
			}),
			col: 'right',
			cards: weatherItemIds.map((id, i) => {
				return {
					id: genWidgetId(id, widgetType, i),
					position: i,
				};
			}),
		});
	});
};

export const convertLegacyToNewChartWidget = (dashID) => {
	const widgetType: DashWidgetTypes = 'dashChart';

	return [
		widgetCardsDataMapper[widgetType]({
			id: genWidgetId(dashID, widgetType),
			title: '',
			type: widgetType,
			col: 'left',
		}),
	];
};

export const convertLegacyToNewMediaWidget = (deviceList, dashID: string) => {
	if (!deviceList || deviceList?.length === 0) {
		return [];
	}

	const widgetType: DashWidgetTypes = 'dashMedia';
	const mediaPlayerItems = getNumericKeys(deviceList).filter((key) => {
		return get(devices)[key] && get(devices)[key]?.category === 'media_player';
	});

	return mediaPlayerItems.map((id, i) => {
		return widgetCardsDataMapper[widgetType]({
			id: genWidgetId(dashID, widgetType, i),
			position: deviceList[id]?.position || 1,
			type: widgetType,
			title: '',
			col: 'right',
			cards: mediaPlayerItems.map((id, i) => {
				return {
					id,
					position: i,
				};
			}),
		});
	});
};

export const convertLegacyToNewRoomWidget = (roomsList, dashID: string) => {
	if (!roomsList || roomsList?.length === 0) {
		return [];
	}

	const widgetType: DashWidgetTypes = 'dashRoom';
	const roomIds = getNumericKeys(roomsList).filter((id) => get(rooms)[id]);
	const roomChunks = genChunks(roomIds);

	return roomChunks.map((roomsChunk, i) => {
		return widgetCardsDataMapper[widgetType]({
			id: genWidgetId(dashID, widgetType, i),
			position: 3,
			type: widgetType,
			title: get(format)('label_rooms_data_card', {
				default: 'Rooms',
			}),
			cards: roomsChunk.map((id, i) => {
				return {
					id,
					position: i,
				};
			}),
			col: 'right',
		});
	});
};

export const convertLegacyToNewGroupWidget = (groupsList, dashID: string) => {
	if (!groupsList || groupsList?.length === 0) {
		return [];
	}

	const widgetType: DashWidgetTypes = 'dashGroup';
	const groupsIds = getNumericKeys(groupsList).filter((id) => get(groups)[id]);
	const groupChunks = genChunks(groupsIds);

	return groupChunks.map((groupChunk, i) => {
		return widgetCardsDataMapper[widgetType]({
			id: genWidgetId(dashID, widgetType, i),
			position: 3,
			type: widgetType,
			title: get(format)('label_groups_data_card', {
				default: 'Groups',
			}),
			cards: groupChunk.map((id, i) => {
				return {
					id,
					position: i,
				};
			}),
			col: 'right',
		});
	});
};

export const convertLegacyToNewSceneWidget = (scenesList, dashID: string) => {
	if (!scenesList || scenesList?.length === 0) {
		return [];
	}

	const widgetType: DashWidgetTypes = 'dashScene';
	const scenesIds = getNumericKeys(scenesList).filter((id) => get(scenes)[id]);
	const scenesChunks = genChunks(scenesIds);

	return scenesChunks.map((sceneChunk, i) => {
		return widgetCardsDataMapper[widgetType]({
			id: genWidgetId(dashID, widgetType, i),
			position: 4,
			type: widgetType,
			title: get(format)('label_scenes_data_card', {
				default: 'Scenes',
			}),
			cards: sceneChunk.map((id, i) => {
				return {
					id,
					position: i,
				};
			}),
			col: 'right',
		});
	});
};

export const convertLegacyToNewAlarmWidget = (alarmsList, dashID: string) => {
	if (!alarmsList || alarmsList?.length === 0) {
		return [];
	}

	const widgetType: DashWidgetTypes = 'dashAlarm';
	const alarmsIds = getNumericKeys(alarmsList).filter((id) => get(alarms)[id]);
	const alarmsChunks = genChunks(alarmsIds);

	return alarmsChunks.map((sceneChunk, i) => {
		return widgetCardsDataMapper[widgetType]({
			id: genWidgetId(dashID, widgetType, i),
			position: 4,
			type: widgetType,
			title: get(format)('label_alarms_data_card', {
				default: 'Alarms',
			}),
			cards: sceneChunk.map((id, i) => {
				return {
					id,
					position: i,
				};
			}),
			col: 'right',
		});
	});
};

export const convertLegacyToNewDeviceWidget = (devicesList, dashID: string) => {
	if (!devicesList || devicesList?.length === 0) {
		return [];
	}

	const widgetType: DashWidgetTypes = 'dashQuickDev'; // Default type it can be also dashControl
	const devicesIds = getNumericKeys(devicesList).filter((id) => {
		return deviceWithNoMediaCondition(id);
	});

	const deviceChunks = genChunks(devicesIds);

	return deviceChunks.map((deviceChunk, i) => {
		return widgetCardsDataMapper[widgetType]({
			id: genWidgetId(dashID, widgetType, i),
			position: 1,
			type: widgetType,
			title: '',
			cards: deviceChunk.map((id, i) => {
				return {
					id,
					position: i,
				};
			}),
			col: 'left',
		});
	});
};

export const convertLegacyToNewThermostatWidget = (thermostatList, dashID: string) => {
	if (!thermostatList || thermostatList?.length === 0) {
		return [];
	}

	const widgetType: DashWidgetTypes = 'dashThermostat';
	const thermostastIds = getNumericKeys(thermostatList).filter((id) => get(temp_schemes)[id]);
	const thermostatChunks = genChunks(thermostastIds);

	return thermostatChunks.map((thermostatChunk, i) => {
		return widgetCardsDataMapper[widgetType]({
			id: genWidgetId(dashID, widgetType, i),
			position: 4,
			type: widgetType,
			title: get(format)('label_thermostats_data_card', {
				default: 'Thermostats',
			}),
			cards: thermostatChunk.map((id, i) => {
				return {
					id,
					position: i,
				};
			}),
			col: 'right',
		});
	});
};
//#endregion Convert Legacy to new dash

//#startregion Gen new dash widget data
export const genAccInfoData = (widget) => {
	return {
		id: widget?.id,
		title: widget?.title,
		showWidgetTitle: widget?.showWidgetTitle,
		isDndShadowItem: false,
		type: widget?.type,
		cards: [
			{
				_type: 'acc_info',
				id: 'consumption',
			},
			{
				_type: 'acc_info',
				id: 'allDevices',
			},
			{
				_type: 'acc_info',
				id: 'offlineDevices',
			},
			{
				_type: 'acc_info',
				id: 'sharedDevices',
			},
			{
				_type: 'acc_info',
				id: 'recentlySharedDevices',
			},
			{
				_type: 'acc_info',
				id: 'hiddenDevices',
			},
		],
		col: widget?.col,
	};
};

export const genChartData = (widget) => {
	return {
		id: widget?.id,
		title: widget?.title,
		showWidgetTitle: widget?.showWidgetTitle,
		isDndShadowItem: false,
		type: widget?.type,
		cards: [
			{
				_type: 'chart',
				id: 'overalConsumption',
			},
		],
		col: widget?.col,
	};
};

export const genWeatherCardData = (widget) => {
	return {
		id: widget?.id,
		position: widget?.position,
		isDndShadowItem: false,
		type: widget.type,
		title: widget.title,
		showWidgetTitle: widget?.showWidgetTitle,
		cards: widget?.cards.map((weatherCard) => {
			return {
				id: weatherCard?.id,
				position: weatherCard?.position,
				_type: 'weather',
			};
		}),
		col: widget.col,
	};
};

export const genRoomCardData = (widget) => {
	return {
		id: widget?.id,
		position: widget?.position,
		isDndShadowItem: false,
		type: widget?.type,
		title: widget?.title,
		showWidgetTitle: widget?.showWidgetTitle,
		cards: widget.cards
			.map((roomData: any, i) => {
				const room = get(rooms)[roomData.id];
				// Currently, we intentionally exclude item that are missing.
				// This behavior is subject to change if we decide to show missing item
				if (!room) return null;
				return {
					id: roomData?.id,
					position: roomData?.position || i,
					_type: 'rooms',
				};
			})
			.filter(isNonNullable),
		col: widget?.col,
	};
};

export const genGroupCardData = (widget) => {
	return {
		id: widget?.id,
		position: widget?.position,
		type: widget?.type,
		title: widget?.title,
		showWidgetTitle: widget?.showWidgetTitle,
		isDndShadowItem: false,
		cards: widget?.cards
			?.map((groupData: any) => {
				const group = get(groups)[groupData?.id];
				// Currently, we intentionally exclude item that are missing.
				// This behavior is subject to change if we decide to show missing item
				if (!group) return null;

				return {
					id: groupData?.id,
					value: !group ? groupData?.id : group?.name,
					position: groupData?.position,
					...(widget?.type === 'dashLight' && { bg: groupData?.bg }),
					_type: 'groups',
				};
			})
			.filter(isNonNullable),
		col: widget?.col,
	};
};

export const genSceneCardData = (widget) => {
	return {
		id: widget?.id,
		position: widget?.position,
		type: widget?.type,
		title: widget?.title,
		showWidgetTitle: widget?.showWidgetTitle,
		isDndShadowItem: false,
		cards: widget?.cards
			?.map((sceneData: any) => {
				const scene = get(scenes)[sceneData?.id];
				// Currently, we intentionally exclude item that are missing.
				// This behavior is subject to change if we decide to show missing item
				if (!scene) return null;

				return {
					id: sceneData?.id,
					position: sceneData?.position,
					_type: 'scenes',
				};
			})
			.filter(isNonNullable),
		col: widget?.col,
	};
};

export const genAlarmCardData = (widget) => {
	return {
		id: widget?.id,
		position: widget?.position,
		type: widget?.type,
		title: widget?.title,
		showWidgetTitle: widget?.showWidgetTitle,
		isDndShadowItem: false,
		cards: widget?.cards
			?.map((alarmData: any) => {
				const alarm = get(alarms)[alarmData?.id];
				// Currently, we intentionally exclude item that are missing.
				// This behavior is subject to change if we decide to show missing item
				if (!alarm) return null;

				return {
					id: alarmData?.id,
					position: alarmData?.position,
					_type: 'alarms',
				};
			})
			.filter(isNonNullable),
		col: widget?.col,
	};
};

export const genDeviceCardData = (widget) => {
	let widgetType: DashWidgetTypes = widget?.type;

	// Convert already saved dashParameterDev / dashCardDev widgets to dashQuickDev.
	// Button cards (`dashCardDev`) become whole-card Control cards (no button placement).
	// Editing a widget updates its type to dashQuickDev in DB.
	if (widgetType === 'dashParameterDev' || widgetType === 'dashCardDev') {
		widgetType = 'dashQuickDev';
	}

	const deviceCardConfig =
		widgetType === 'dashQuickDev' ? normalizeDeviceCardConfig(widget?.deviceCardConfig) : undefined;

	return {
		id: widget?.id,
		position: widget?.position,
		isDndShadowItem: false,
		type: widgetType,
		title: widget?.title,
		showWidgetTitle: widget?.showWidgetTitle,
		cards: widget.cards
			.map((deviceData) => {
				const device = get(devices)[deviceData.id];
				// Currently, we intentionally exclude item that are missing.
				// This behavior is subject to change if we decide to show missing item
				if (!device) return null;

				return {
					id: deviceData?.id,
					position: deviceData?.position,
					_type: 'devices',
				};
			})
			.filter(isNonNullable),
		col: widget?.col,
		...(widget?.cameraConfig && {
			cameraConfig: normalizeCameraConfig(widget.cameraConfig),
		}),
		// Only persist button placement; default card mode omits the field on save,
		// but load always normalizes so render gets an explicit placement.
		...(deviceCardConfig?.controlPlacement === 'button' && {
			deviceCardConfig: { controlPlacement: 'button' as const },
		}),
	};
};

export const genCameraCardData = (widget) => {
	return {
		...genDeviceCardData(widget),
		cameraConfig: normalizeCameraConfig(widget?.cameraConfig || {}),
	};
};

export const genThermostatCardData = (widget) => {
	return {
		id: widget?.id,
		position: widget?.position,
		type: widget?.type,
		title: widget?.title,
		showWidgetTitle: widget?.showWidgetTitle,
		isDndShadowItem: false,
		cards: widget?.cards
			?.map((thermostatData: any) => {
				const thermostat = get(temp_schemes)[thermostatData?.id];
				// Currently, we intentionally exclude item that are missing.
				// This behavior is subject to change if we decide to show missing item
				if (!thermostat) return null;
				return {
					id: thermostatData?.id,
					position: thermostatData?.position,
					_type: 'temp_schemes',
				};
			})
			.filter(isNonNullable),
		col: widget?.col,
	};
};

export const widgetCardsDataMapper: {
	[key in DashWidgetTypes]: (widget) => any;
} = {
	dashWeather: genWeatherCardData,
	dashMedia: genDeviceCardData,
	dashParameterDev: genDeviceCardData,
	dashQuickDev: genDeviceCardData,
	dashCardDev: genDeviceCardData,
	dashControlDev: genDeviceCardData,
	dashCamera: genCameraCardData,
	dashGroup: genGroupCardData,
	dashLight: genGroupCardData,
	dashRoom: genRoomCardData,
	dashInfo: genAccInfoData,
	dashChart: genChartData,
	dashScene: genSceneCardData,
	dashAlarm: genAlarmCardData,
	dashThermostat: genThermostatCardData,
	...(Object.fromEntries(
		DASH_ENERGY_WIDGET_TYPES.map((type) => [type, genEnergyWidgetData]),
	) as Record<DashEnergyWidgetTypes, typeof genEnergyWidgetData>),
	dashEnvironment: genDeviceCardData,
	dashPromo: (widget) => ({
		...widget,
		cards: widget?.cards?.length
			? widget.cards
			: [{ id: `${widget?.id ?? 'dash-promo'}-card`, _type: 'promo', position: 0 }],
	}),
};

export const mapDashItemsToDashWidgets = (dash, id) => {
	if (dash?.dashWidgets) {
		return dash?.dashWidgets.map((widget) => {
			return widgetCardsDataMapper[widget.type]?.(widget);
		});
	}

	return [
		// Default widgets should be created for initial default created dashboard.
		...(id?.toString() === DEFAULT_DASH_ID.toString()
			? [...convertLegacyToNewAccInfoWidget(id), ...convertLegacyToNewChartWidget(id)]
			: []),
		...convertLegacyToNewDeviceWidget(dash.devices, id),
		...convertLegacyToNewWeatherWidget(dash.weather, id),
		...convertLegacyToNewMediaWidget(dash.devices, id),
		...convertLegacyToNewRoomWidget(dash.rooms, id),
		...convertLegacyToNewGroupWidget(dash.groups, id),
		...convertLegacyToNewSceneWidget(dash.scenes, id),
		...convertLegacyToNewAlarmWidget(dash.alarms, id),
		...convertLegacyToNewThermostatWidget(dash.temp_schemes, id),
	];
};
//#endregion Gen new dash widget data

export async function requestReorderDashboardWidget(dashboardId, editedList) {
	const url = '/interface/dash/update';

	const updatedDashboard = {
		...get(dashboards)[dashboardId],
		dashWidgets: [...Object.values(editedList)],
	};

	const res = await postReq(url, {
		id: dashboardId,
		data: JSON.stringify(updatedDashboard),
	});

	if (res.isok) {
		dashboards.update({
			...get(dashboards),
			[dashboardId]: updatedDashboard,
		});
	} else {
		// toast.open({type:'danger', title:$format('error_global')});
	}
}

export const getInitOldToNewDashConvertWidgets = (currentDashboard, dashboardId) => {
	const allWidgets = mapDashItemsToDashWidgets(currentDashboard, dashboardId);
	let initialDashWidgets = [];

	allWidgets.forEach((v, i) => {
		delete v?.isDndShadowItem;
		initialDashWidgets = [
			...initialDashWidgets,
			{
				...v,
				position: i,
				cards: v.cards.map((item, y) => {
					return {
						id: item.id,
						_type: item._type,
						position: y,
					};
				}),
			},
		];
	});

	return initialDashWidgets;
};

export async function requestAddDashItem(dashWidget: DashWidgetEditType) {
	const url = '/interface/dash/update';
	const dashboardId = dashWidget.dashboardId;
	let currentDashboard = { ...get(dashboards)[dashboardId] };
	const allDashWidgets = currentDashboard.dashWidgets || [];

	const allWidgetsFromCurentType = allDashWidgets.filter(
		(widget) => widget.type === dashWidget.widgetType,
	);

	const allWidgetsFromColumn = allDashWidgets.filter((widget) => widget.col === dashWidget.col);

	const maxCurrentIdSufix = allWidgetsFromCurentType.length
		? Math.max(...allWidgetsFromCurentType.map((item) => +item.id.split('-').pop() || 0))
		: 0;

	const lastPosition = allWidgetsFromColumn.length
		? Math.max(...allWidgetsFromColumn.map((item) => +item.position))
		: 0;

	const currentWidget = {
		cards: dashWidget.selectItems.map((item, i) => {
			return {
				id: item.id,
				_type: item.itemType,
				position: i,
				...(dashWidget.widgetType === 'dashLight' && {
					bg: item?.bg || getRandomDashLightWidgetBg(),
				}),
			};
		}),
		col: dashWidget?.col || 'left',
		id: dashWidget.id || genWidgetId(dashboardId, dashWidget.widgetType, maxCurrentIdSufix + 1),
		title: dashWidget.widgetTitle,
		showWidgetTitle: dashWidget.showWidgetTitle,
		type: dashWidget.widgetType,
		...(dashWidget.cameraConfig &&
			(dashWidget.widgetType === 'dashCamera' ||
				dashWidget.widgetType === 'dashCardDev' ||
				dashWidget.widgetType === 'dashQuickDev') && {
				cameraConfig: normalizeCameraConfig(dashWidget.cameraConfig),
			}),
		...(dashWidget.widgetType === 'dashQuickDev' &&
			normalizeDeviceCardConfig(dashWidget.deviceCardConfig).controlPlacement === 'button' && {
				deviceCardConfig: { controlPlacement: 'button' as const },
			}),
		...(dashWidget.energyConfig &&
			isDashEnergyWidgetType(dashWidget.widgetType) && {
				energyConfig: dashWidget.energyConfig,
			}),
		position: dashWidget.position ?? lastPosition + 1,
	};

	//Handle edit widget
	if (dashWidget.id) {
		const editWidgetIndex = allDashWidgets.findIndex((widget) => widget.id === dashWidget.id);

		if (editWidgetIndex !== -1) {
			currentDashboard.dashWidgets[editWidgetIndex] = currentWidget;
		}
	}

	//Handle add new item to converted dash for first time and no reorder made
	if (!currentDashboard?.dashWidgets) {
		currentDashboard.dashWidgets = getInitOldToNewDashConvertWidgets(currentDashboard, dashboardId);
	}

	const updatedDashboard = {
		...currentDashboard,
		dashWidgets: dashWidget.id
			? [...currentDashboard.dashWidgets]
			: [...currentDashboard.dashWidgets, currentWidget],
	};

	const res = await postReq(url, {
		id: dashboardId,
		data: JSON.stringify(updatedDashboard),
	});

	if (res.isok) {
		dashboards.update({
			...get(dashboards),
			[dashboardId]: updatedDashboard,
		});
	} else {
		// toast.open({type:'danger', title:$format('error_global')});
	}
}

export async function requestDeleteDashboardWidget(dashWidget: DashWidgetEditType) {
	const url = '/interface/dash/update';
	const dashboardId = dashWidget.dashboardId;
	let currentDashboard = { ...get(dashboards)[dashboardId] };

	//Handle add new item to converted dash for first time and no reorder made
	if (!currentDashboard?.dashWidgets) {
		currentDashboard.dashWidgets = getInitOldToNewDashConvertWidgets(currentDashboard, dashboardId);
	}

	const allDashWidgets = currentDashboard.dashWidgets || [];
	const filteredDashItems = allDashWidgets.filter((item) => item.id !== dashWidget?.id);

	const updatedDashboard = {
		...currentDashboard,
		dashWidgets: [...filteredDashItems],
	};

	const res = await postReq(url, {
		id: dashboardId,
		data: JSON.stringify(updatedDashboard),
	});

	if (res.isok) {
		dashboards.update({
			...get(dashboards),
			[dashboardId]: updatedDashboard,
		});
	} else {
		// toast.open({type:'danger', title:$format('error_global')});
	}
}

export const createNewDashWithDefaultWidgets = async (currentDashboard, dashID: string) => {
	const allWidgets = getInitOldToNewDashConvertWidgets(currentDashboard, dashID);
	let dashWidgets = {};

	allWidgets.map((v, i) => {
		delete v?.isDndShadowItem;
		dashWidgets[v.id] = {
			...v,
			position: i,
			cards: v.cards.map((item, y) => {
				return {
					id: item.id,
					_type: item._type,
					position: y,
				};
			}),
		};
	});

	await requestReorderDashboardWidget(dashID, dashWidgets);
};

export const getWidgetTitleProposal = (type: DashWidgetTypes) => {
	if (isDashEnergyWidgetType(type)) return getEnergyWidgetTitleProposal(type);

	const previewItemsTitleMapper: Partial<Record<DashWidgetTypes, string>> = {
		dashParameterDev: get(format)('dash_widget_device_proposal', {
			default: 'Devices',
		}),
		dashControlDev: get(format)('dash_widget_device_proposal', {
			default: 'Devices',
		}),
		dashQuickDev: get(format)('dash_widget_device_proposal', {
			default: 'Devices',
		}),
		dashCardDev: get(format)('dash_widget_device_proposal', {
			default: 'Devices',
		}),
		dashCamera: get(format)('dash_widget_camera_proposal', {
			default: 'Camera',
		}),
		dashRoom: get(format)('dash_widget_rooms_proposal', { default: 'Rooms' }),
		dashScene: get(format)('dash_widget_scenes_proposal', {
			default: 'Scenes',
		}),
		dashAlarm: get(format)('dash_widget_alarms_proposal', {
			default: 'Alarms',
		}),
		dashGroup: get(format)('dash_widget_groups_proposal', {
			default: 'Groups',
		}),
		dashChart: get(format)('dash_widget_chart_proposal', { default: 'Chart' }),
		dashInfo: get(format)('dash_widget_acc_info_proposal', {
			default: 'Account Info',
		}),
		dashLight: get(format)('dash_widget_lights_proposal', {
			default: 'Light Control',
		}),
		dashMedia: get(format)('dash_widget_media_proposal', { default: 'Media' }),
		dashWeather: get(format)('dash_widget_weather_proposal', {
			default: 'Weather',
		}),
		dashThermostat: get(format)('dash_widget_thermostats_proposal', {
			default: 'Thermostats',
		}),
		dashEnvironment: get(format)('dash_widget_environment_proposal', {
			default: 'Environment',
		}),
		dashPromo: get(format)('dash_widget_promo_proposal', {
			default: 'Promotion',
		}),
	};

	return previewItemsTitleMapper[type] ?? type;
};

//Guard when we get bolean or string with witespace value for image frim DB
export const safeTrim = (value: unknown): string => {
	return typeof value === 'string' ? value.trim() || undefined : undefined;
};

/**
 * Maps a dashboard to RectangularAvatar props.
 * `icon` and `backgroundColor` coexist: the token paints the plate (and page bg),
 * the icon sits on top. Legacy image-only dashes still resolve to `image`.
 */
export const mapDashToAvatar = (
	dash: any,
	defautBG: AvatarColorVariantTypes | undefined = undefined,
) => {
	const icon = dash?.icon?.trim() || undefined;
	const backgroundColor = (dash?.backgroundColor?.trim() || defautBG) as
		| AvatarColorVariantTypes
		| undefined;
	const image =
		dash?.image?.trim() && dash.image !== '/[image_url]'
			? load_image_from(dash.image.trim())
			: undefined;

	if (icon || backgroundColor) {
		return {
			icon: icon || 'fat fa-objects-column',
			backgroundColor,
		};
	}

	if (image) {
		return { image };
	}

	return {
		icon: 'fat fa-objects-column',
		backgroundColor: defautBG,
	};
};

export function genDeviceWidgetCardDescription(state) {
	if (!get(edit)) {
		if (state?.type !== defaultStateType) {
			return {
				value: state?.description?.value,
				type: state?.description.type,
			};
		} else {
			return undefined;
		}
	}
}

export function getAdditionalText(
	device: Device,
	isUpdating: boolean,
	hasTimer: boolean,
	time: number | undefined,
	deviceState: Record<string, any> | undefined,
	criticalNotifications,
): DashDeviceWidgetPropsTypes['additionalText'] {
	if (!device)
		return {
			value: get(format)('label_missing_device', {
				default: 'Missing Device',
			}),
			type: 'warning',
		};

	if (isUpdating)
		return {
			value: get(format)('device_status_updating', {
				default: 'Device is updating',
			}),
			type: 'primary',
		};

	if (hasTimer && time)
		return {
			value: formatTimerCountdown(time),
			icon: 'far fa-clock',
			type: 'primary',
		};

	if (criticalNotifications?.length && deviceState?.type === defaultStateType)
		return { value: criticalNotifications?.[0].name, type: 'warning' };

	const description = genDeviceWidgetCardDescription(deviceState);
	return {
		value: description?.value,
		type: description?.type === 'disabled' ? 'muted' : description?.type,
	};
}

export function getAvatar(device: Device) {
	if (!device) return { icon: 'fat fa-triangle-exclamation text-color-warning' };

	const url = device?.localImage ? '' : device?.server;
	return {
		image: device?.image ? load_image_from(device?.image, url).replace('thumb_', '') : undefined,
		backgroundColor: device?.backgroundColor ? device.backgroundColor : null,
		icon: mapOldCustomIconFromDBtoNew(device?.icon),
		noBackground: !device?.backgroundColor,
	};
}

// Check if ID is a valid value (including 0, but excluding undefined/null/empty string)
export const hasDashValidId = (id: any): boolean => id !== undefined && id !== null && id !== '';

export const getDashRouteHref = (dashboards, dashHref: string) => {
	// Go to empty dash list when user doesn't have dash
	if (dashboards.length === 0) {
		return dashHref;
	}

	// Order by position and get first dash. We may have dash on first postion that is bigger than 0
	const dashFirstPosition = dashboards.sort(sortByPosition)?.[0] as any;

	return hasDashValidId(dashFirstPosition?.id) ? `${dashHref}/${dashFirstPosition.id}` : dashHref;
};

// Timer Helpers

export function formatTimerCountdown(seconds: number): string {
	const { hours, minutes, seconds: secs } = convertSeconds(seconds);
	return hours > 0
		? `${formatTwoDigits(hours)}:${formatTwoDigits(minutes)}:${formatTwoDigits(secs)}`
		: `${formatTwoDigits(minutes)}:${formatTwoDigits(secs)}`;
}
